import { useCallback, useMemo, useState } from 'react'

import {
  TOpportunitiesV3Item,
  useGetOpportunitiesV3ListInfiniteQuery,
  useGetOpportunitiesV3SummaryQuery
} from 'integration/resources/opportunitiesV3'
import {
  defaultFilters,
  useOpportunitiesV3FiltersAtom
} from 'src/store/screens/opportunitiesV3Filters'
import { calculateActiveFilterCount } from 'src/utils/filters'
import { LogEvent } from 'src/utils/logEvents'

import { UseOpportunitiesListScreen } from './OpportunitiesListScreen.types'

export const useOpportunitiesListScreen: UseOpportunitiesListScreen = ({ navigation }) => {
  const [prioritySelected, setPrioritySelected] = useState<number | undefined>(undefined)

  const [filtersState, setFiltersState] = useOpportunitiesV3FiltersAtom()

  const { data: opportunitiesHeaderData, isLoading: isLoadingOpportunitiesHeader } =
    useGetOpportunitiesV3SummaryQuery()

  const opportunitiesHeader = opportunitiesHeaderData?.data

  const {
    data: opportunitiesListData,
    isLoading: isLoadingOpportunitiesList,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch: refetchOpportunitiesList
  } = useGetOpportunitiesV3ListInfiniteQuery({
    per_page: 10,
    priority: filtersState.priority,
    offer: filtersState.offer,
    public_rating: filtersState.public_rating,
    fidelity: filtersState.fidelity,
    segment: filtersState.segment,
    network: filtersState.network,
    subsidiary: filtersState.subsidiary,
    user_std_code: filtersState.user_std_code,
    order_by: filtersState.order_by,
    order_by_direction: filtersState.order_by_direction
  })

  const opportunitiesList = useMemo(
    () =>
      opportunitiesListData?.pages.reduce<TOpportunitiesV3Item[]>(
        (previousValue, currentValue) => [...previousValue, ...(currentValue.data.data || [])],
        []
      ) ?? [],
    [opportunitiesListData]
  )

  const totalItems = opportunitiesListData?.pages[0]?.data.metadata.pagination.total ?? 0

  const fetchMore = useCallback(() => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage()
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  const handleSelectPriority = (priority: number) => {
    const newPriority = priority === prioritySelected ? undefined : priority

    setPrioritySelected(newPriority)

    setFiltersState({
      ...filtersState,
      priority: newPriority ? [newPriority] : []
    })
  }

  const modalFilters = useMemo(
    () => ({
      priority: filtersState.priority,
      offer: filtersState.offer,
      public_rating: filtersState.public_rating,
      fidelity: filtersState.fidelity,
      segment: filtersState.segment,
      network: filtersState.network,
      subsidiary: filtersState.subsidiary,
      user_std_code: filtersState.user_std_code
    }),
    [filtersState]
  )

  const activeFilterCount = calculateActiveFilterCount(modalFilters)

  const hasFilter = activeFilterCount > 0

  const handleGoToFilters = () => {
    LogEvent(`oportunidades_odc_acessar_filtros`, {
      item_id: ''
    })

    navigation.navigate('OpportunitiesV3Filter')
  }

  const handleGoToDetails = (id: string, type: TOpportunitiesV3Item['type']) => {
    navigation.navigate('OpportunitiesDetails', { id, type })
  }

  const handleRemoveFilter = (key: string, raw?: string) => {
    const current = modalFilters[key as keyof typeof modalFilters]

    if (!Array.isArray(current)) {
      setFiltersState({ ...filtersState, [key]: undefined })

      return
    }

    const next = (current as (string | number)[]).filter(
      (value: string | number) => String(value) !== raw
    )

    setFiltersState({ ...filtersState, [key]: next })

    if (key === 'priority') {
      setPrioritySelected(undefined)
    }
  }

  const handleResetFilters = () => {
    setFiltersState(defaultFilters)
  }

  return {
    prioritySelected,
    handleSelectPriority,
    opportunitiesHeader,
    isLoadingOpportunitiesHeader,
    handleGoToFilters,
    activeFilterCount,
    hasFilter,
    activeFilters: modalFilters,
    handleRemoveFilter,
    handleResetFilters,
    handleGoToDetails,
    opportunitiesList,
    totalItems,
    isLoadingOpportunitiesList,
    isFetchingNextPage,
    fetchMore,
    refetchOpportunitiesList
  }
}
